import { useInfiniteQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import React, { FC, useCallback, useEffect, useRef } from "react";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";

import { TableHeader } from "~/components/ui/table";

import { getListProjectsQueryKey, listProjects } from "~/api/folders/folders";
import { useGetOrganization } from "~/api/organizations/organizations";
import { Loader } from "~/components/Loader";
import { PaymentRequiredBanner } from "~/features/billing/PaymentRequiredBanner";
import { ROUTES, useOrganizationParams } from "~/helpers";
import { CaseListItem } from "./CaseListItem";
import { CreateCaseButton } from "./CreateCaseButton";
import { EmptyCasesList } from "./EmptyCasesList";

interface TableHeader {
  id: string;
  defaultMessage: string;
  className?: string;
}

const ITEMS_PER_PAGE = 50;

const COLUMN_WIDTHS = {
  name: "w-[25%]",
  status: "w-[8%]",
  dateOfBirth: "w-[10%]",
  dateOfLoss: "w-[10%]",
  claimNumber: "w-[10%]",
  created: "w-[10%]",
  modified: "w-[10%]",
  createdBy: "w-[12%]",
  actions: "w-[5%]",
} as const;

export const CasesList: FC = () => {
  const navigate = useNavigate();
  const { organizationId } = useOrganizationParams();
  const observerTarget = useRef<HTMLDivElement>(null);

  const {
    data: casesData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
  } = useInfiniteQuery({
    queryKey: getListProjectsQueryKey(organizationId, { archived: false }),
    queryFn: async ({ pageParam = 1 }) => {
      const response = await listProjects(organizationId, {
        archived: false,
        page: pageParam as number,
        limit: ITEMS_PER_PAGE,
      });
      return response;
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage?.pagination) return undefined;
      if (lastPage.pagination.currentPage < lastPage.pagination.totalPages) {
        return lastPage.pagination.currentPage + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
  });

  const { data: organizationInfo } = useGetOrganization(
    organizationId as string
  );

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [target] = entries;
      if (target.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage]
  );

  useEffect(() => {
    const element = observerTarget.current;
    if (!element) return;

    const option = {
      root: null,
      rootMargin: "0px",
      threshold: 0.5,
    };

    const observer = new IntersectionObserver(handleObserver, option);
    observer.observe(element);

    return () => observer.disconnect();
  }, [handleObserver]);

  const isPaymentRequired =
    organizationInfo?.serviceStatus === "PAYMENT_REQUIRED";

  const tableHeaders: TableHeader[] = [
    {
      id: "casesList.name",
      defaultMessage: "Name",
      className: COLUMN_WIDTHS.name,
    },
    {
      id: "casesList.status",
      defaultMessage: "Status",
      className: COLUMN_WIDTHS.status,
    },
    {
      id: "dateOfBirthLabel",
      defaultMessage: "Date of Birth",
      className: COLUMN_WIDTHS.dateOfBirth,
    },
    {
      id: "dateOfLossLabel",
      defaultMessage: "Date of Loss",
      className: COLUMN_WIDTHS.dateOfLoss,
    },
    {
      id: "claimNumberLabel",
      defaultMessage: "Claim or Case #",
      className: COLUMN_WIDTHS.claimNumber,
    },
    {
      id: "casesList.created",
      defaultMessage: "Created",
      className: COLUMN_WIDTHS.created,
    },
    {
      id: "casesList.modified",
      defaultMessage: "Last Modify",
      className: COLUMN_WIDTHS.modified,
    },
    {
      id: "casesList.createdBy",
      defaultMessage: "Created By",
      className: COLUMN_WIDTHS.createdBy,
    },
    { id: "", defaultMessage: "", className: COLUMN_WIDTHS.actions },
  ];

  const onCaseClick = (caseId?: string) =>
    caseId && navigate(ROUTES.CASE({ organizationId, caseId }));

  if (isLoading)
    return (
      <div className="flex justify-center items-center h-full">
        <Loader />
      </div>
    );

  if (isError)
    return (
      <div className="flex justify-center items-center h-full">
        <p>
          <FormattedMessage
            id="casesList.failedFetchingCases"
            defaultMessage={
              "Couldn't fetch the list of cases. Please try again"
            }
          />
        </p>
      </div>
    );

  const isFirstCase =
    !casesData || casesData.pages.flatMap((page) => page.projects).length === 0;

  if (
    !casesData ||
    casesData.pages.flatMap((page) => page.projects).length === 0
  )
    return (
      <>
        {isPaymentRequired && (
          <div className="p-6">
            <PaymentRequiredBanner />
          </div>
        )}
        <EmptyCasesList disabled={isPaymentRequired} />
      </>
    );

  return (
    <div>
      {isPaymentRequired && (
        <div className="p-6">
          <PaymentRequiredBanner />
        </div>
      )}
      <div className="flex justify-between items-center px-6 pt-6 pb-8">
        <h2 className="text-2xl font-semibold">
          <FormattedMessage id="casesList.title" defaultMessage={"Cases"} />
        </h2>
        <div className="cursor-pointer">
          <CreateCaseButton
            disabled={isPaymentRequired}
            isFirstCase={isFirstCase}
          />
        </div>
      </div>
      <div className="rounded-md px-6">
        <div className="flex items-center border-b bg-gray-50 py-3">
          {tableHeaders.map((header) => (
            <div
              key={header.id}
              className={`px-4 text-sm font-medium text-zinc-500 ${header.className}`}
            >
              {header.id && (
                <FormattedMessage
                  id={header.id}
                  defaultMessage={header.defaultMessage}
                />
              )}
            </div>
          ))}
        </div>
        <div>
          {casesData?.pages.map((page, i) => (
            <React.Fragment key={i}>
              {(Array.isArray(page) ? page : page?.projects || []).map(
                (caseItem) => (
                  <CaseListItem
                    key={caseItem.id}
                    caseItem={caseItem}
                    onCaseClick={onCaseClick}
                    columnWidths={COLUMN_WIDTHS}
                  />
                )
              )}
            </React.Fragment>
          ))}
          <div ref={observerTarget} className="h-10" />
          {isFetchingNextPage && (
            <div className="flex justify-center py-4">
              <Loader2 className="w-4 h-4 animate-spin text-gray-300" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
